/**
 * The chat thread's scroll, one hook for the admin chat screen and the client's (Gabriel
 * 2026-10-06: shared so the two never drift), bottom-anchored (chat-thread-bottom-anchored-scroll).
 * Every scroll number comes from lib/chat/threadScroll; the screens only use what this returns.
 *
 * - The scroller is a `column-reverse` flex container (D1): `scrollTop` is 0 at the newest entry
 *   and negative upward. Older history joining above the view, the keyboard resizing the
 *   scroller and a send's landing slide move nothing on screen, so none of them writes a scroll.
 * - Pinned to the newest entry while the reader is within the slack of it; scrolling up unpins,
 *   coming back within the slack pins again. Older history is requested four screens before the
 *   oldest loaded entry, from the distance to it.
 * - The rest gate (D3, D8): while the reader is unpinned and moving (a scroll in the last
 *   REST_MS, or a finger on the glass) the gate is held, and the data hooks park what they fetch.
 *   It opens at rest (`scrollend`, or REST_MS of silence with no finger down) and on a pin.
 * - The place (D4): the entry at the top of the view and its offset, re-captured after every
 *   scroll and every resize of either box. The hook writes `scrollTop` in two cases only: while
 *   unpinned and at rest, to hold the anchored entry when content changes in or below the view
 *   (falling to its nearest older neighbour when a commit removed it), and while pinned, to snap
 *   to 0 when the newest end grows. Its own writes are flagged, so their scroll events never
 *   count as the reader moving.
 * - A stale step (staleStepCorrection): when history joins in the same frame as a scroll step the
 *   browser worked out before it, the view leaps a page toward the oldest entries; when the held
 *   entry drifts by what joined above it, just after it joined, it is put back, the reader's own step
 *   kept.
 * - A saved place (savePlace, before a door opens another page) carries a return token, the room
 *   and the lens; the data hook keeps its thread under that token (D5) and seeds it on the way
 *   back, so the place is restored in the first layout from the entries present: the anchored
 *   entry at its offset, or the saved distance from the bottom when it is gone.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ThreadRoom } from '@telify/shared/browser';
import {
  AT_REST, STALE_MS, anchorCorrection, distanceToTop, firstVisible, fromBottom, isAtRest, isPinned, nearestSurvivingAnchor, nextPinned,
  restDueIn, restState, scrollDirection, shouldLoadOlder, staleStepCorrection, type Anchor, type EntryBox, type ScrollDirection,
  type ScrollGesture,
} from '@/lib/chat/threadScroll';

/** Where the reader was when a door opened another page. */
export interface SavedPlace<Lens extends string = string> {
  /** The return token: the thread the data hook kept on leaving is stored under it. */
  token: string;
  room: ThreadRoom;
  lens: Lens;
  anchorId: string | null;
  anchorOffset: number;
  /** Distance above the newest entry: the fallback when the anchored entry is gone. */
  fromBottom: number;
  pinned: boolean;
}

export interface ThreadScrolled {
  /** This frame's step: down toward the newest entry, up toward older history (4 px dead zone). */
  direction: ScrollDirection;
  fromBottom: number;
  distanceToTop: number;
}

/** A step this small is no direction (the chips ignore rubber-band jitter). */
const DIRECTION_STEP_PX = 4;

/**
 * The rest gate between the scroll hook and a thread data hook (D3). Both screens call the data
 * hook first (the scroll hook needs its entries), so the screen creates the gate and hands it to
 * both: the scroll hook drives it, the data hook reads `held()` and listens with `onRest`.
 */
export class ChatThreadGate {
  private isHeld = false;
  private readonly listeners = new Set<() => void>();
  /** The token savePlace minted last: the data hook keeps its thread under it on unmount. */
  returnToken: string | null = null;

  /** True while the reader is away from the newest entry and the thread is moving. */
  held(): boolean {
    return this.isHeld;
  }

  /** Runs `listener` each time the gate opens (rest or a pin); returns the unsubscribe. */
  onRest(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** The scroll hook's: opening the gate tells every listener. */
  setHeld(next: boolean): void {
    const opened = this.isHeld && !next;
    this.isHeld = next;
    if (opened) this.listeners.forEach((listener) => listener());
  }

  /** The scroll hook's, for another room or lens: open without telling anyone (what waited is dropped). */
  reset(): void {
    this.isHeld = false;
  }
}

/** One gate per mounted screen. */
export function useChatThreadGate(): ChatThreadGate {
  return useState(() => new ChatThreadGate())[0];
}

interface ChatThreadScrollOptions<Lens extends string> {
  gate: ChatThreadGate;
  entries: readonly { id: string }[];
  hasOlder: boolean;
  loadOlder: () => unknown;
  /** The room and lens on screen, recorded in a saved place. */
  room: ThreadRoom;
  lens: Lens;
  /** The place to come back to (read on the first layout only); a pinned place opens at the newest entry. */
  restore?: SavedPlace<Lens> | null;
  /** Runs on each scroll frame after pinning and paging (the admin's chips fold on it). */
  onScrolled?: (scroll: ThreadScrolled) => void;
}

/**
 * The anchor and the entries measured with it (oldest first, relative to the view's top, ending at
 * it), and how far the anchor sits below the top of the stream (what joins above it, for a stale step).
 */
interface Place {
  anchor: Anchor;
  boxes: EntryBox[];
  above: number | null;
}

function entryNodes(el: HTMLElement): HTMLElement[] {
  return Array.from(el.querySelectorAll<HTMLElement>('[data-entry-id]'));
}

function topOf(el: HTMLElement, id: string | null): number | null {
  const node = id === null ? undefined : entryNodes(el).find((item) => item.dataset.entryId === id);
  return node ? node.getBoundingClientRect().top : null;
}

/** The entry at the top of the view, with the boxes read on the way to it. */
function measure(el: HTMLElement, slack: number, inner: HTMLElement | null = null): Place | null {
  const viewTop = el.getBoundingClientRect().top;
  const boxes: EntryBox[] = [];
  function* read(): Generator<EntryBox> {
    for (const node of entryNodes(el)) {
      const rect = node.getBoundingClientRect();
      const box = { id: node.dataset.entryId ?? '', top: rect.top - viewTop, bottom: rect.bottom - viewTop };
      boxes.push(box);
      yield box;
    }
  }
  const anchor = firstVisible(0, read(), slack);
  if (!anchor) return null;
  return { anchor, boxes, above: inner ? viewTop + anchor.offset - inner.getBoundingClientRect().top : null };
}

function newToken(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

export function useChatThreadScroll<Lens extends string>({
  gate, entries, hasOlder, loadOlder, room, lens, restore, onScrolled,
}: ChatThreadScrollOptions<Lens>) {
  const threadRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const pinnedRef = useRef(true);
  /** The offset at the previous scroll event (pinning) and at the previous frame (direction). */
  const lastTopRef = useRef(0);
  const frameTopRef = useRef(0);
  const frameRef = useRef(0);
  const restRef = useRef(AT_REST);
  const restTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** The offset this hook last wrote: the scroll event that write fires is not the reader. */
  const ownWriteRef = useRef<number | null>(null);
  const placeRef = useRef<Place | null>(null);
  const oldestIdRef = useRef<string | null>(null);
  /** When older history last joined above a reader up in the history: a stale step can follow it. */
  const joinedAtRef = useRef<number | null>(null);
  const restoreRef = useRef(restore && !restore.pinned ? restore : null);
  const firstLayoutRef = useRef(true);
  const newestIdRef = useRef<string | null>(null);
  const onScrolledRef = useRef(onScrolled);
  onScrolledRef.current = onScrolled;
  const entriesRef = useRef(entries);
  entriesRef.current = entries;
  const pagingRef = useRef({ hasOlder, loadOlder });
  pagingRef.current = { hasOlder, loadOlder };
  const keyRef = useRef({ room, lens });
  keyRef.current = { room, lens };

  const moving = useCallback(() => !isAtRest(restRef.current, Date.now()), []);
  const updateHeld = useCallback(() => gate.setHeld(!pinnedRef.current && moving()), [gate, moving]);

  const write = useCallback((el: HTMLElement, top: number) => {
    if (Math.abs(el.scrollTop - top) < 1) return;
    el.scrollTop = top;
    ownWriteRef.current = el.scrollTop;
    lastTopRef.current = el.scrollTop;
    frameTopRef.current = el.scrollTop;
  }, []);

  const capture = useCallback((el: HTMLElement) => {
    placeRef.current = measure(el, 1, innerRef.current);
  }, []);

  /** Holds the reading place after content changed in or below the view; only while unpinned and at rest. */
  const holdPlace = useCallback((el: HTMLElement) => {
    const place = placeRef.current;
    if (!place) return;
    const anchor = nearestSurvivingAnchor(place.anchor.id, place.boxes, entriesRef.current);
    const top = anchor ? topOf(el, anchor.id) : null;
    if (!anchor || top === null) return;
    const delta = anchorCorrection(top, el.getBoundingClientRect().top, anchor.offset);
    if (delta !== 0) write(el, el.scrollTop + delta);
  }, [write]);

  /**
   * A stale step (staleStepCorrection): soon after older history joined, the entry the reader holds
   * drifted down by what joined above it since its place was measured, as if the view had kept its
   * distance from the oldest end; it goes back, the reader's own step kept. Until then the place is
   * not re-measured on a resize or a commit, only after a scroll (which checks first).
   */
  const fixStaleStep = useCallback((el: HTMLElement) => {
    const joinedAt = joinedAtRef.current;
    const place = placeRef.current;
    const inner = innerRef.current;
    if (joinedAt === null || !place || place.above === null || !inner) return;
    if (Date.now() - joinedAt >= STALE_MS) {
      joinedAtRef.current = null;
      return;
    }
    const top = topOf(el, place.anchor.id);
    if (top === null) return;
    const grew = top - inner.getBoundingClientRect().top - place.above;
    const fix = staleStepCorrection(grew, top - el.getBoundingClientRect().top - place.anchor.offset);
    if (fix === 0) return;
    joinedAtRef.current = null;
    write(el, el.scrollTop + fix);
  }, [write]);

  const askOlder = useCallback((el: HTMLElement) => {
    const { hasOlder: more, loadOlder: load } = pagingRef.current;
    if (more && shouldLoadOlder(distanceToTop(el.scrollTop, el.scrollHeight, el.clientHeight), el.clientHeight)) void load();
  }, []);

  /** The thread came to rest: the gate opens and the place is measured as it now stands. */
  const settle = useCallback(() => {
    restTimerRef.current = null;
    const el = threadRef.current;
    if (el && !pinnedRef.current) capture(el);
    updateHeld();
  }, [capture, updateHeld]);

  const noteGesture = useCallback((gesture: ScrollGesture) => {
    const now = Date.now();
    restRef.current = restState(restRef.current, gesture, now);
    if (restTimerRef.current !== null) clearTimeout(restTimerRef.current);
    restTimerRef.current = null;
    updateHeld();
    const due = restDueIn(restRef.current, now);
    if (due === null) return;
    if (due === 0) settle();
    else restTimerRef.current = setTimeout(settle, due);
  }, [settle, updateHeld]);

  const onScroll = useCallback(() => {
    const el = threadRef.current;
    if (!el) return;
    const own = ownWriteRef.current;
    ownWriteRef.current = null;
    if (own !== null && Math.abs(el.scrollTop - own) < 1) return;
    fixStaleStep(el);
    pinnedRef.current = nextPinned(pinnedRef.current, el.scrollTop, lastTopRef.current);
    lastTopRef.current = el.scrollTop;
    noteGesture('scroll');
    if (frameRef.current) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = 0;
      const node = threadRef.current;
      if (!node) return;
      const prev = frameTopRef.current;
      frameTopRef.current = node.scrollTop;
      capture(node);
      askOlder(node);
      onScrolledRef.current?.({
        direction: scrollDirection(prev, node.scrollTop, DIRECTION_STEP_PX),
        fromBottom: fromBottom(node.scrollTop),
        distanceToTop: distanceToTop(node.scrollTop, node.scrollHeight, node.clientHeight),
      });
    });
  }, [askOlder, capture, fixStaleStep, noteGesture]);

  // The finger and the engine's own end of a scroll (D8): a paused drag keeps the gate closed.
  useEffect(() => {
    const el = threadRef.current;
    if (!el) return undefined;
    const handlers = (['touchstart', 'touchend', 'touchcancel', 'scrollend'] as const).map((gesture) => {
      const handler = () => {
        if (gesture === 'touchstart') ownWriteRef.current = null;
        noteGesture(gesture);
      };
      el.addEventListener(gesture, handler, { passive: true });
      return () => el.removeEventListener(gesture, handler);
    });
    return () => handlers.forEach((remove) => remove());
  }, [noteGesture]);

  useEffect(() => () => {
    cancelAnimationFrame(frameRef.current);
    if (restTimerRef.current !== null) clearTimeout(restTimerRef.current);
  }, []);

  // Sizes change after the entries render (Show more clamps, fonts, a failure line, the keyboard
  // resizing the scroller). The scroller's own resize keeps the bottom edge on its own: no write.
  useEffect(() => {
    const el = threadRef.current;
    const inner = innerRef.current;
    if (!el || !inner || typeof ResizeObserver === 'undefined') return undefined;
    let viewHeight = el.clientHeight;
    let innerHeight = inner.getBoundingClientRect().height;
    const observer = new ResizeObserver(() => {
      fixStaleStep(el);
      const viewChanged = el.clientHeight !== viewHeight;
      const nextInner = inner.getBoundingClientRect().height;
      const grew = nextInner > innerHeight;
      viewHeight = el.clientHeight;
      innerHeight = nextInner;
      if (!viewChanged && !moving()) {
        if (pinnedRef.current) {
          if (grew) write(el, 0);
        } else holdPlace(el);
      }
      if (!pinnedRef.current && joinedAtRef.current === null) capture(el);
    });
    observer.observe(inner);
    observer.observe(el);
    return () => observer.disconnect();
  }, [capture, fixStaleStep, holdPlace, moving, write]);

  useLayoutEffect(() => {
    const el = threadRef.current;
    if (!el) return;
    const newestId = entries[entries.length - 1]?.id ?? null;
    // Older history joined above a reader up in the history: a stale step can follow it.
    const oldestId = entries[0]?.id ?? null;
    if (!firstLayoutRef.current && !pinnedRef.current && oldestId !== oldestIdRef.current) joinedAtRef.current = Date.now();
    oldestIdRef.current = oldestId;
    fixStaleStep(el);
    if (firstLayoutRef.current) {
      // The first layout: a saved place is restored from the entries present, or not at all (a
      // thread that has to load afresh opens at its newest entry).
      firstLayoutRef.current = false;
      const saved = restoreRef.current;
      restoreRef.current = null;
      if (saved && entries.length > 0) {
        pinnedRef.current = false;
        write(el, -saved.fromBottom);
        const top = topOf(el, saved.anchorId);
        if (top !== null) write(el, el.scrollTop + (top - el.getBoundingClientRect().top - saved.anchorOffset));
        pinnedRef.current = isPinned(el.scrollTop);
      }
    } else if (pinnedRef.current) {
      // A new entry at the newest end while pinned: the whole of it on screen.
      if (newestId !== newestIdRef.current) write(el, 0);
    } else if (!moving()) holdPlace(el);
    newestIdRef.current = newestId;
    if (!pinnedRef.current && joinedAtRef.current === null) capture(el);
    askOlder(el);
  }, [askOlder, capture, entries, fixStaleStep, holdPlace, moving, write]);

  /** Pins to the newest entry now and opens the gate (a send is about to land, the composer took focus). */
  const pin = useCallback(() => {
    pinnedRef.current = true;
    const el = threadRef.current;
    if (el) write(el, 0);
    updateHeld();
  }, [updateHeld, write]);

  /** Another room or lens: the new list opens at its newest entry; what waited for rest is dropped. */
  const startNewList = useCallback(() => {
    pinnedRef.current = true;
    restoreRef.current = null;
    placeRef.current = null;
    newestIdRef.current = null;
    restRef.current = AT_REST;
    if (restTimerRef.current !== null) clearTimeout(restTimerRef.current);
    restTimerRef.current = null;
    gate.reset();
    const el = threadRef.current;
    if (el) write(el, 0);
  }, [gate, write]);

  /** Where the reader is now, for the way back from a page a door opens, under a fresh return token. */
  const savePlace = useCallback((): SavedPlace<Lens> => {
    const el = threadRef.current;
    const anchor = el ? measure(el, 0)?.anchor ?? null : null;
    const token = newToken();
    gate.returnToken = token;
    return {
      token,
      room: keyRef.current.room,
      lens: keyRef.current.lens,
      anchorId: anchor?.id ?? null,
      anchorOffset: anchor?.offset ?? 0,
      fromBottom: el ? fromBottom(el.scrollTop) : 0,
      pinned: !el || isPinned(el.scrollTop),
    };
  }, [gate]);

  return { threadRef, innerRef, onScroll, pin, revealNewest: pin, startNewList, savePlace };
}
